import { ResourceProps } from '@/libs/contentful/types';
import { ArticleDisplayTag, RelatedArticleProps, RelatedArticleFlexibleProps } from './types';
import { getRichTextValue } from '@/utils/formatters';
import { extractSlugFromUrl } from '@/libs/contentful/utils';

export const BLOG_POST_FORMAT_TAG = 'Blog';

export const resourceToArticleShim = (
  item: ResourceProps | RelatedArticleProps | RelatedArticleFlexibleProps,
  taxonomyLabel?: string,
  taxonomyUri?: string,
): RelatedArticleProps => {
  const getCategory = (defaultCategory?: { label: string; url?: string }) => {
    if (taxonomyLabel) {
      return { label: taxonomyLabel, url: taxonomyUri };
    }
    return defaultCategory;
  };

  if (item.__typename === 'Resource') {
    const defaultCategory = item?.resourceLabelRte ? { label: getRichTextValue(item.resourceLabelRte) } : undefined;
    // A slug renders relative to the current host, so a www URL shown on enterprise would resolve to enterprise.
    const isNonRelativeUrl = !!item.url && !item.resourcePage && !item.url.startsWith('/') && !item.url.startsWith('#');
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.resourceCopy,
      articleImage: item.resourceIcon,
      slug: isNonRelativeUrl ? '' : item.resourcePage?.slug || extractSlugFromUrl(item.url),
      href: isNonRelativeUrl ? item.url : undefined,
      category: getCategory(defaultCategory),
      labels: item.labels,
      sys: item?.sys || { id: '' },
    };
  }

  if (item.__typename === 'PageBlogPostFlexible') {
    const defaultCategory = item.category ? { label: item.category.label, url: item.category.url } : undefined;
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.articleTitle?.headline,
      articleImage: item.articleImage?.desktopMedia,
      slug: item.slug,
      category: getCategory(defaultCategory),
      categories: item.categories,
      isBlogPost: true,
      sys: item.sys,
      publishDate: item.publishDate,
    };
  }

  const relatedArticle = item as RelatedArticleProps;
  return {
    ...relatedArticle,
    category: getCategory(relatedArticle.category),
  };
};

function categoryTag(
  article: Pick<RelatedArticleProps, 'category'>,
  includeCategoryLinks?: boolean,
): ArticleDisplayTag | undefined {
  if (!article.category?.label) return undefined;
  return {
    label: article.category.label,
    href: includeCategoryLinks ? article.category.url : undefined,
  };
}

function nonemptyLabels(labels: string[] | undefined): string[] {
  if (!labels) return [];
  return labels.map(label => label.trim()).filter(label => label.length > 0);
}

export function getArticleDisplayTags(
  article: RelatedArticleProps,
  options: { showFormatTags?: boolean; includeCategoryLinks?: boolean },
): ArticleDisplayTag[] {
  const category = categoryTag(article, options.includeCategoryLinks);

  if (!options.showFormatTags) {
    return category ? [category] : [];
  }

  if (article.isBlogPost) {
    const tags: ArticleDisplayTag[] = [];
    if (category) tags.push(category);
    tags.push({ label: BLOG_POST_FORMAT_TAG, filled: true });
    return tags;
  }

  const labels = nonemptyLabels(article.labels);
  if (labels.length > 0) {
    return labels.map((label, index) => (index > 0 ? { label, filled: true } : { label }));
  }

  return category ? [category] : [];
}

export function withArticleDisplayTags(
  article: RelatedArticleProps,
  options: { showFormatTags?: boolean; includeCategoryLinks?: boolean },
): RelatedArticleProps {
  return {
    ...article,
    displayTags: getArticleDisplayTags(article, options),
  };
}

export function resolveArticleTags(
  article: Pick<RelatedArticleProps, 'displayTags' | 'category'>,
  includeCategoryLinks?: boolean,
): ArticleDisplayTag[] {
  if (article.displayTags) return article.displayTags;
  const category = categoryTag(article, includeCategoryLinks);
  return category ? [category] : [];
}
